(ns ui.view.kanban-opensearch.outcome-tray
  "The drop tray of the Kanban. While the user drags a card, a tray at the bottom of
   the board has the targets that set the outcome of the card: put it on hold, or
   mark it as lost (a deal is passed). A drop on a target runs the action that the
   menu of the card has for the same outcome, so the curtain of that action opens and
   the stage of the card does not change. The tray and its targets are the ZapKit
   DropTray (`ui.components.drop-tray.view`). This namespace knows when the tray is
   open and what a drop does. The state is in the `ui-state*` atom of the Kanban:
   `:dragging` (the card that is dragged), `:drop-over` (the id of the target that the
   card is over) and `:absorbing` (the target that takes the drop, with its targets,
   for a short time after the drop)."
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   [ui.components.drop-tray.view :as drop-tray]
   [ui.dom-globals :as dg]))

(def absorb-ms
  "How long the target that took a drop stays marked, in milliseconds."
  300)

(def wrapper-class
  "The tray is at the bottom of the board, over the cards, with 16 px of space around it. The
   wrapper does not take the pointer events, so the tray is the only part that takes them."
  "tw2-pointer-events-none tw2-absolute tw2-inset-x-0 tw2-bottom-0 tw2-z-20 tw2-px-4 tw2-pb-4")

(def row-class
  "The targets are in one row, as in the ZapKit story."
  "tw2-flex tw2-w-full tw2-gap-3")

(defn targets-to-show
  "Return the targets that the tray shows, or nil when it is closed. While a card is
   dragged these are the targets of that card: `drop-targets-fn` gives them for the card
   and returns nil when the card has no outcome action. After a drop they are the targets
   that were kept for the time of `absorb-ms`."
  [{:keys [dragging absorbing]} drop-targets-fn]
  (cond
    (and dragging (fn? drop-targets-fn)) (seq (drop-targets-fn (:card dragging)))
    absorbing (seq (:targets absorbing))))

(defn drag-ended
  "The state after the drag of a card ends, with or without a drop."
  [state]
  (assoc state
         :dragging nil
         :drop-target-column nil
         :drop-over nil))

(defn over
  "The state when the dragged card is over the target `id`."
  [state id]
  (assoc state :drop-over id))

(defn left
  "The state when the dragged card leaves the target `id`. A card that has already
   moved over another target is not touched."
  [state id]
  (if (= id (:drop-over state))
    (assoc state :drop-over nil)
    state))

(defn dropped
  "The state after a drop on the target `id`. The targets stay for the settle."
  [state {:keys [id targets]}]
  (assoc state
         :drop-over nil
         :absorbing {:id      id
                     :targets targets}))

(defn settled
  "The state after the drop has settled."
  [state]
  (dissoc state :absorbing))

(defn target-component
  "The component of a target by its id: `:on-hold` or `:lost`."
  [id]
  (if (= :on-hold id)
    drop-tray/on-hold-target
    drop-tray/lost-target))

(defn view
  "The tray. `:targets` is a vector of maps with `:id` (`:on-hold` or `:lost`) and
   `:label`. `:over` is the id of the target that the card is over, and `:absorbing` is
   the id of the target that takes the drop. `:on-over` and `:on-leave` get the id of a
   target. `:on-drop` gets the id of the target that took the drop."
  [{:keys [targets over absorbing on-over on-leave on-drop]}]
  [:div {:class wrapper-class}
   (into [drop-tray/view
          {:message (loc/tr :drop-tray-message)
           :class   "tw2-pointer-events-auto"}]
         [(into [:div {:class row-class}]
                (for [{:keys [id label]} targets]
                  ^{:key (name id)}
                  [(target-component id)
                   {:label         label
                    :active?       (= over id)
                    :absorbing?    (= absorbing id)
                    :on-drag-over  (fn [e]
                                     (dg/prevent-default e)
                                     (on-over id))
                    :on-drag-leave (fn [_]
                                     (on-leave id))
                    :on-drop       (fn [e]
                                     (dg/prevent-and-stop e)
                                     (on-drop id))}]))])])

(defn container
  "The tray of a Kanban. It reads `ui-state*` itself, so that only this component
   renders again when the drag or the target that the card is over changes, and not
   the whole Kanban. `drop-targets-fn` gives the targets for a card, see
   `targets-to-show`. A drop runs the `:on-drop` of the target, marks the target
   for `absorb-ms`, and the drag of the card ends as usual. The targets that stay for the
   settle do not take a drop: a drop runs an action only while a card is dragged."
  [{:keys [ui-state* drop-targets-fn]}]
  (let [ui @ui-state*
        targets (some-> (targets-to-show ui drop-targets-fn) vec)]
    (when targets
      [view
       {:targets   targets
        :over      (:drop-over ui)
        :absorbing (get-in ui [:absorbing :id])
        :on-over   (fn [id]
                     (swap! ui-state* over id))
        :on-leave  (fn [id]
                     (swap! ui-state* left id))
        :on-drop   (fn [id]
                     (when-let [target (and (:dragging @ui-state*)
                                            (cu/find-first-by :id id targets))]
                       (swap! ui-state* dropped {:id      id
                                                 :targets targets})
                       ((:on-drop target))
                       (dg/set-timeout! (fn []
                                          (swap! ui-state* settled))
                                        absorb-ms)))}])))
